import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Pencil, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { authFetch, apiErr } from "../../lib/adminApi";
import { AdminBar } from "./AdminCore";
import { ImageDrop, imgSrc, inputCls, labelCls, FieldEditor } from "./EditorFields";

const API = process.env.REACT_APP_BACKEND_URL;
const JOURNAL_KEYS = ["property_type", "manufacturer", "product", "brief", "design_objectives", "spec_rationale",
  "technical", "challenges", "outcome", "gallery", "video", "related_products", "related_area", "related_articles"];
const BLANK = { title: "", location: "", spec: "", body: "", image: "" };

const JOURNAL_DEFS = [
  { k: "property_type", t: "text", label: "Property / project type" },
  { k: "manufacturer", t: "text", label: "Manufacturer" },
  { k: "product", t: "text", label: "Product / system" },
  { k: "related_area", t: "text", label: "Related area slug (e.g. guildford)" },
  { k: "brief", t: "textarea", label: "Client brief" },
  { k: "design_objectives", t: "textarea", label: "Design objectives" },
  { k: "spec_rationale", t: "textarea", label: "Specification rationale" },
  { k: "technical", t: "textarea", label: "Technical / installation considerations" },
  { k: "challenges", t: "textarea", label: "Challenges" },
  { k: "outcome", t: "textarea", label: "Project outcome" },
  { k: "gallery", t: "images", label: "Photo gallery" },
  { k: "video", t: "text", label: "Video embed URL (YouTube embed link)" },
  { k: "related_products", t: "items", label: "Related products", fields: [
    { k: "label", t: "text", label: "Label" }, { k: "to", t: "text", label: "Link (e.g. /origin/)" }] },
  { k: "related_articles", t: "items", label: "Related articles / guides", fields: [
    { k: "label", t: "text", label: "Label" }, { k: "to", t: "text", label: "Link" }] },
];

function CsForm({ initial, onDone, onCancel, testPrefix }) {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [showJournal, setShowJournal] = useState(JOURNAL_KEYS.some((k) => {
    const v = initial[k];
    return Array.isArray(v) ? v.length : v;
  }));
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const editing = !!initial.id;

  const save = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) { toast.error("Please add a title"); return; }
    setBusy(true);
    try {
      const payload = { title: f.title, location: f.location, spec: f.spec, body: f.body, image: f.image };
      JOURNAL_KEYS.forEach((k) => { if (f[k] !== undefined) payload[k] = f[k]; });
      const res = await authFetch(editing ? `/api/case-studies/${initial.id}` : "/api/case-studies", {
        method: editing ? "PUT" : "POST",
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(apiErr(data.detail)); return; }
      toast.success(editing ? "Case study updated" : "Case study added — it's now live on the homepage & Projects page");
      onDone();
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} className="border border-white/12 p-8 grid grid-cols-1 md:grid-cols-2 gap-5 mb-10" data-testid={`${testPrefix}-form`}>
      <div>
        <label className={labelCls}>Project title *</label>
        <input value={f.title} onChange={set("title")} data-testid={`${testPrefix}-title-input`} placeholder="e.g. Origin Bifolds & Sliders, Woking" className={inputCls} />
      </div>
      <div>
        <label className={labelCls}>Location</label>
        <input value={f.location} onChange={set("location")} data-testid={`${testPrefix}-location-input`} placeholder="e.g. Woking, Surrey" className={inputCls} />
      </div>
      <div className="md:col-span-2">
        <label className={labelCls}>Products / spec line</label>
        <input value={f.spec} onChange={set("spec")} data-testid={`${testPrefix}-spec-input`} placeholder="e.g. Origin OB-49 bifolds & OS-44 sliders" className={inputCls} />
      </div>
      <div className="md:col-span-2">
        <label className={labelCls}>Story (shown on the Projects page)</label>
        <textarea value={f.body} onChange={set("body")} rows={3} data-testid={`${testPrefix}-body-input`} className={inputCls} />
      </div>
      <div className="md:col-span-2">
        <label className={labelCls}>Photo — drop an image or click to browse</label>
        <ImageDrop value={f.image} onChange={(url) => setF({ ...f, image: url })} testid={`${testPrefix}-image-drop`} />
      </div>
      <div className="md:col-span-2 border-t border-white/10 pt-5">
        <button type="button" onClick={() => setShowJournal(!showJournal)} data-testid={`${testPrefix}-journal-toggle`}
          className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#5B7F9E] hover:text-white transition-colors">
          {showJournal ? "− Hide" : "+ Develop into a"} full project journal page {showJournal ? "" : "(optional)"}
        </button>
        {showJournal && (
          <div className="mt-6 space-y-5">
            <p className="text-white/40 text-xs leading-relaxed">Fill in any of these and this project gets its own page at /projects/…/ — linked from its card and included in the sitemap. Leave them empty to keep it as a simple card.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {JOURNAL_DEFS.slice(0, 4).map((d) => (
                <FieldEditor key={d.k} def={d} prefix={`${testPrefix}-journal`} value={f[d.k]} onChange={(v) => setF({ ...f, [d.k]: v })} />
              ))}
            </div>
            {JOURNAL_DEFS.slice(4).map((d) => (
              <FieldEditor key={d.k} def={d} prefix={`${testPrefix}-journal`} value={f[d.k]} onChange={(v) => setF({ ...f, [d.k]: v })} />
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center gap-3 md:col-span-2">
        <button type="submit" disabled={busy} data-testid={`${testPrefix}-save-btn`} className="btn-solid disabled:opacity-40">
          <Plus size={14} /> {busy ? "Saving…" : editing ? "Save changes" : "Add case study"}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} data-testid={`${testPrefix}-cancel-btn`}
            className="px-4 py-2 text-xs font-semibold tracking-[0.15em] uppercase text-white/60 hover:text-white transition-colors">Cancel</button>
        )}
      </div>
    </form>
  );
}

export default function AdminCaseStudies() {
  const [items, setItems] = useState([]);
  const [editId, setEditId] = useState(null);

  const load = useCallback(() => {
    fetch(`${API}/api/case-studies`).then((r) => r.json()).then(setItems).catch(() => {});
  }, []);

  useEffect(() => { document.title = "Admin · Case Studies | Cedar Views"; load(); }, [load]);

  const remove = async (cs) => {
    if (!window.confirm(`Delete "${cs.title}"? It disappears from the site immediately.`)) return;
    const res = await authFetch(`/api/case-studies/${cs.id}`, { method: "DELETE" });
    if (res.ok) { toast.success("Case study deleted"); load(); }
    else toast.error("Could not delete");
  };

  const move = async (i, dir) => {
    const next = [...items];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
    const res = await authFetch("/api/case-studies-order", { method: "PUT", body: JSON.stringify(next.map((x) => x.id)) });
    if (!res.ok) { toast.error("Could not save the new order"); load(); }
  };

  return (
    <main className="pt-40 pb-24 min-h-screen bg-[#050A15]" data-testid="admin-case-studies">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
        <AdminBar title="Case Studies." />
        <p className="text-white/50 text-sm mb-8 -mt-4 max-w-2xl">
          The first three appear on the homepage under &ldquo;Case Studies&rdquo;; the full list appears on the Projects page. Drag-free reordering with the arrows — top of the list shows first.
        </p>

        <CsForm initial={BLANK} onDone={load} testPrefix="cs-add" />

        <div className="border border-white/12 divide-y divide-white/10" data-testid="cs-list">
          {items.map((cs, i) => (
            <div key={cs.id}>
              <div className="flex items-center justify-between px-6 py-4 gap-4" data-testid={`cs-row-${i}`}>
                <div className="flex items-center gap-4 min-w-0">
                  {cs.image ? (
                    <img src={imgSrc(cs.image)} alt="" className="w-16 h-12 object-cover border border-white/15 shrink-0" />
                  ) : (
                    <div className="w-16 h-12 bg-white/5 border border-white/10 shrink-0" />
                  )}
                  <div className="min-w-0">
                    <p className="text-white font-medium text-sm truncate">{cs.title}</p>
                    <p className="font-mono-x text-[10px] text-white/35 mt-1 truncate">{cs.location}{cs.spec ? ` · ${cs.spec}` : ""}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => move(i, -1)} disabled={i === 0} className="p-2 text-white/40 hover:text-white disabled:opacity-20" title="Move up"><ChevronUp size={15} /></button>
                  <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="p-2 text-white/40 hover:text-white disabled:opacity-20" title="Move down"><ChevronDown size={15} /></button>
                  <button onClick={() => setEditId(editId === cs.id ? null : cs.id)} data-testid={`cs-edit-${i}`}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-white/80 hover:bg-white hover:text-[#0B162C] text-xs font-semibold tracking-[0.12em] uppercase transition-colors">
                    <Pencil size={13} /> Edit
                  </button>
                  <button onClick={() => remove(cs)} data-testid={`cs-delete-${i}`} className="p-2 text-white/50 hover:text-red-400 transition-colors" title="Delete">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
              {editId === cs.id && (
                <div className="px-6 pb-6">
                  <CsForm initial={cs} onDone={() => { setEditId(null); load(); }} onCancel={() => setEditId(null)} testPrefix={`cs-edit-${i}`} />
                </div>
              )}
            </div>
          ))}
          {items.length === 0 && <p className="px-6 py-8 text-white/40 text-sm">No case studies yet — add your first above.</p>}
        </div>
      </div>
    </main>
  );
}
